import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {GameAssets} from "../GameAssets";
import {Game} from "../Game";
import {BasicBitmapCountUp} from "../components/BasicBitmapCountUp";
import {gsap} from "gsap";
import {ContinueButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import {ScreenBounds} from "@nolimitcity/slot-launcher/bin/display/ScreenBounds";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {GameFeatureMode} from "../enums/Enum";
import {BitmapText} from "pixi.js";
import {GameUtils} from "../utils/GameUtils";
import {Sound} from "../sound/Sound";

export class WinCapView extends PIXI.Container implements StageLayer {
    private _winCapBg: PIXI.Graphics;
    private _winCapSpine: PIXI.spine.Spine;
    private _countUp: BasicBitmapCountUp;
    private _winCapTimes: BitmapText;
    private _button: ContinueButton;
    private _buttonCallBack: Function;
    private _endLoopTl: gsap.core.Timeline;
    private _wincapTl: gsap.core.Timeline;
    private _oldScreenSpine: PIXI.spine.Spine;
    private _preCountUpLevels: PIXI.spine.Spine[] = [];
    private _preCountUpAnimations: string[] = ["animation", "animation", "front_walk"];
    private _stopped: boolean = false;
    private _hideWinCapTL: gsap.core.Timeline | null;

    constructor() {
        super();
        this.name = "WinCapView";
        this.init();
    }

    public playWinCapAnimations(callBack: Function): gsap.core.Timeline {
        this._stopped = false;
        const tl: gsap.core.Timeline = gsap.timeline();

        this._countUp.alpha = 0;
        this._countUp.y = 580;

        tl.add(() => {
            this._buttonCallBack = callBack;
            Game.keypadPlugin.hide();
            Game.keypadPlugin.setZeroBetSpinCounter(-1);

            this.alpha = 0;
            this.visible = true;
            this._winCapBg.visible = true;
            this._winCapTimes.visible = true;
            this._winCapSpine.visible = true;
            this._winCapTimes.alpha = 0;
            GameUtils.resetSpineItem(this._winCapSpine);
            // #342
            Game.soundPlayer.playSound(Sound.WIN_CAP_START);

            this._oldScreenSpine.state.setAnimation(0, "desktop", true);
        }, 0);

        this._wincapTl = gsap.timeline();
        tl.to(this, {alpha: 1, duration: 0.6});

        tl.add(Game.bigWinView.hide(0.1)); // #353, removed big win view's visibility check to match the sound and animation, as this hide method returns some time which is required always.

        this._preCountUpLevels.forEach((preCountUp: PIXI.spine.Spine, index: number) => {
            tl.add([
                () => {
                    this._preCountUpLevels[index].visible = true;
                },
                GameUtils.createTimelineForAnimation(preCountUp, 0, this._preCountUpAnimations[index], false)
            ]);
        });

        tl.add([
            () => {
                this._countUp.alpha = 1;
            },
            GameUtils.createTimelineForAnimation(this._winCapSpine, 0, "animation", false),
            this._countUp.getCountAnimation((1), Game.model.gameData.accumulatedRoundWin, 5.69999) //#420 changed 1000 to 1 in from params
        ]);

        // #342
        tl.add(() => {
            Game.soundPlayer.switchMusic("WIN_CAP_LOOP");
        });

        tl.to(this._countUp, {
            duration: 0.3,
            alpha: 0
        });

        //looping animation after wincap countup
        tl.add(this._wincapTl);

        tl.add(() => {
            if (Game.model.data.mode === GameFeatureMode.RESPIN) {
                Game.backgroundElements.glitchReSpinToMain();
            }
            Game.blackBG.hide(0.01);

            // #344
            Game.keypadPlugin.setWin(Game.model.gameData.accumulatedRoundWin, true, Game.isWinBelowStake());

            Game.keypadPlugin.show();
            this.endLoop(true);
        });

        tl.add(() => {
            this.showButton();
        }, "+=1.283");

        return tl;
    }

    public onResize(): void {
        if (this.visible) {
            const bounds: ScreenBounds = NolimitApplication.screenBounds;
            if (NolimitApplication.isLandscape) {
                this._button.position.set(360, 625);
                this._winCapSpine.scale.set(0.7);
                this._oldScreenSpine.scale.set(0.71);
                this._preCountUpLevels.forEach((preCountUpLevel: PIXI.spine.Spine) => {
                    preCountUpLevel.scale.set(0.7);
                })
            } else {
                const centerBottom = (bounds.bottom - 610) * 0.5 + 610;
                this._button.position.set(360, centerBottom - 80);
                this._winCapSpine.scale.set(0.65);
                this._oldScreenSpine.scale.set(0.65);
                this._preCountUpLevels.forEach((preCountUpLevel: PIXI.spine.Spine) => {
                    preCountUpLevel.scale.set(0.65);
                })
            }
        }
    }

    public showButton() {
        const tl: gsap.core.Timeline = gsap.timeline();
        tl.add(() => {
            this._button.enable(true);
            this._button.alpha = 1;
            this._button.visible = true;
        })
    }

    private init() {
        this._winCapBg = new PIXI.Graphics();
        this._winCapBg.visible = false;
        this._winCapBg.beginFill(0x000000);
        this._winCapBg.drawRect(-480, -480, 1680, 1680);
        this._winCapBg.endFill();

        this._winCapSpine = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("wincap"));
        this._winCapSpine.visible = false;
        this._winCapSpine.position.set(360, 360);
        this._winCapSpine.scale.set(NolimitApplication.isLandscape ? 0.7 : 0.65);
        this._countUp = new BasicBitmapCountUp({
            fontName: GameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize: 52,
        }, 580, new PIXI.Point(0.5, 0.25));
        this._countUp.position.set(360, 580);

        this._button = new ContinueButton();
        this._button.addClickCallback(() => this.onButtonClick());
        this._button.pivot.set(this._button.width * 0.5, this._button.height * 0.5);
        this._button.enable(false);
        this._button.visible = false;
        const centerBottom: number = (NolimitApplication.screenBounds.bottom - 610) * 0.5 + 610;
        this._button.position.set(360, NolimitApplication.isLandscape ? 625 : centerBottom - 80);

        this._winCapTimes = new BitmapText(Game.apiPlugin.translations.translate("99999x"), {
            fontName: GameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize: 24,
            maxWidth: 580
        });
        this._winCapTimes.position.set(360, 480);
        this._winCapTimes.anchor = 0.5;
        this._winCapTimes.alpha = 0;
        this._winCapTimes.visible = false;
        this._winCapTimes.name = "wincapTimesValue";

        this._oldScreenSpine = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("oldScreenEffect"));
        this._oldScreenSpine.position.set(360);
        this._oldScreenSpine.name = "oldScreenEffect";
        this._oldScreenSpine.scale.set(NolimitApplication.isLandscape ? 0.71 : 0.65);

        this.visible = false;
        this.addChild(this._winCapBg);
        this.createPreCountupLevels();
        this.addChild(this._winCapSpine, this._oldScreenSpine, this._countUp, this._winCapTimes, this._button);
    }

    private onButtonClick() {
        this._button.enable(false);

        const hideWinCapTL: gsap.core.Timeline = new gsap.core.Timeline();
        hideWinCapTL.add(() => {
            Game.soundPlayer.playSound(Sound.CONTINUE_BUTTON_WIN_CAP);
            Game.soundPlayer.switchMusic(GameFeatureMode.NORMAL);
            Game.soundPlayer.fadeSound(0, 1, 1);

            gsap.to(this._button, {duration: 0.1, alpha: 0});
        }, 0);
        hideWinCapTL.to(this, {alpha: 0, duration: 0.3}, 0);
        if (Game.model.data.mode !== GameFeatureMode.NORMAL && Game.model.data.mode !== GameFeatureMode.RESPIN) {
            Game.stickyView.removeStickyStuffs();
            hideWinCapTL.add(() => {
                Game.soundPlayer.playSound(Sound.ELEVATOR_FROM_SFS_TO_MAIN);
            });
            hideWinCapTL.add(Game.backgroundElements.playLiftAnim(Game.model.data.nextMode as GameFeatureMode));
            hideWinCapTL.add(() => {
                Game.blackBG.interactive = true;
                Game.blackBG.buttonMode = true;
                Game.blackBG.show(0.001);
                Game.blackBG.once('pointerdown', () => {
                    this.stopPostOutro();
                });
            }, "<1.7")
        }

        hideWinCapTL.add(Game.reelView.show(0.3));

        hideWinCapTL.add(() => {
            this._endLoopTl.pause();
            this._endLoopTl.kill();
            this._wincapTl.pause();
            this._wincapTl.kill();
            this._preCountUpLevels.forEach(preCountUpLevel => preCountUpLevel.visible = false);
            GameUtils.resetSpineItem(this._winCapSpine);
            GameUtils.resetSpineItem(this._oldScreenSpine);
            Game.winHighlightView.visible = true;
        });

        hideWinCapTL.add(() => {
            this.visible = false;
            this._buttonCallBack();
        });
        this._hideWinCapTL = hideWinCapTL;
    }

    private stopPostOutro(): void {
        if (this._stopped) {
            return;
        }

        if (this._hideWinCapTL && this._hideWinCapTL.isActive()) {
            this._hideWinCapTL.totalProgress(1);
            Game.soundPlayer.stopSound(Sound.ELEVATOR_FROM_SFS_TO_MAIN);
            this._hideWinCapTL = null;
        }
        Game.blackBG.interactive = false;
        Game.blackBG.buttonMode = false;
        Game.blackBG.hide(0.001);
        //Game.apiPlugin.settings.set(APISetting.USE_SPACE_TO_SPIN, this._prevSpaceBarSettings);
        this._stopped = true;
    }

    private endLoop(isFirstTime: boolean = false): gsap.core.Timeline {
        this._endLoopTl = gsap.timeline({
            onComplete: () => {
                this.endLoop();
            }
        });
        const duration: number = 1.283;
        if (isFirstTime) {
            this._endLoopTl.to(this._winCapTimes.scale, {
                duration: 0.2,
                x: 1.8,
                y: 1.8
            }, 0);
            this._endLoopTl.to(this._winCapTimes, {
                duration: 0.2,
                alpha: 1
            }, 0);
            this._endLoopTl.to(this._winCapTimes.scale, {
                duration: 0.1,
                x: 1.5,
                y: 1.5
            });
            this._endLoopTl.to({}, {
                duration: 0.8
            })
        } else {
            this._endLoopTl.to(this._winCapTimes.scale, {
                duration: duration,
                x: 1,
                y: 1
            }, 0);
            this._endLoopTl.to(this._winCapTimes, {
                duration: duration,
                alpha: 0.3
            }, 0);
            this._endLoopTl.to(this._winCapTimes, {
                duration: duration,
                alpha: 1
            }, duration);
            this._endLoopTl.to(this._winCapTimes.scale, {
                duration: duration,
                x: 1.2,
                y: 1.2
            }, duration);
        }
        this._endLoopTl.play(0);

        return this._endLoopTl;
    }

    private createPreCountupLevels(): void {
        const totalLevels: number = 2;
        for (let i: number = 0; i <= totalLevels; i++) {
            let spine: PIXI.spine.Spine = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("preCountUp" + i));
            spine.visible = false;
            spine.name = "preCountUp" + i;
            spine.position.set(360, 360);
            spine.scale.set(NolimitApplication.isLandscape ? 0.7 : 0.65);
            this.addChild(spine);
            this._preCountUpLevels.push(spine);
        }
    }
}